<script setup>
/* eslint-disable vue/custom-event-name-casing, vue/no-bare-strings-in-template */
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import ConversationAPI from 'dashboard/api/inbox/conversation';
import KanbanCard from './KanbanCard.vue';
import { useI18n } from 'vue-i18n';
const props = defineProps({
  label: { type: [String, null], required: true }, // null = "Sin etiqueta" column
  title: { type: String, required: true }, // display name (label or "Sin etiqueta")
  realCount: { type: [Number, null], default: null }, // from label_counts; null => loading
  countStatus: { type: String, default: 'loading' }, // 'loading' | 'ready' | 'error'
  filters: { type: Object, default: () => ({}) }, // full scope
  collapsed: { type: Boolean, default: false }, // minimized column (no cards loaded)
  attentionLabels: { type: Array, default: () => [] },
  refreshToken: { type: Number, default: 0 },
});

const emit = defineEmits([
  'drop-card',
  'card-click',
  'toggle-collapse',
  'move-column',
]);

const { t } = useI18n();

const PER_PAGE = 25;

const cards = ref([]);
const page = ref(1);
const loading = ref(false);
const failed = ref(false);
let generation = 0;
let controller;
let pendingRefresh = false;
let failedRefresh = false;
const endReached = ref(false);
const sentinel = ref(null);

let seenIds = new Set();
let observer = null;

const headerCount = computed(() => {
  if (props.countStatus === 'ready' && props.realCount != null) {
    return String(props.realCount);
  }
  if (props.countStatus === 'error') return String(cards.value.length);
  return '…'; // loading — NEVER show a false 0
});

let refreshExisting;
const fetchNext = async () => {
  if (props.collapsed || loading.value || endReached.value) return;
  loading.value = true;
  failed.value = false;
  const requestGeneration = generation;
  controller = new AbortController();
  try {
    const response = await ConversationAPI.getKanbanColumn({
      label: props.label,
      page: page.value,
      perPage: PER_PAGE,
      ...props.filters,
      signal: controller.signal,
    });
    if (generation !== requestGeneration) return;
    const payload = response?.data?.data?.payload || [];
    payload.forEach(conversation => {
      if (!seenIds.has(conversation.id)) {
        seenIds.add(conversation.id);
        cards.value.push(conversation);
      }
    });
    if (payload.length < PER_PAGE) {
      endReached.value = true;
    }
    page.value += 1;
  } catch (error) {
    if (generation === requestGeneration && !controller?.signal.aborted)
      failed.value = true;
  } finally {
    if (generation === requestGeneration) {
      loading.value = false;
      if (pendingRefresh) refreshExisting();
    }
  }
};

refreshExisting = async () => {
  if (props.collapsed) {
    pendingRefresh = true;
    return;
  }
  if (loading.value) {
    pendingRefresh = true;
    return;
  }
  pendingRefresh = false;
  failedRefresh = false;
  failed.value = false;
  loading.value = true;
  generation += 1;
  const requestGeneration = generation;
  controller?.abort();
  controller = new AbortController();
  const requestedPages = Math.max(1, page.value - 1);
  const refreshed = new Map();
  let reachedEnd = false;
  let nextPage = 1;
  try {
    for (let number = 1; number <= requestedPages; number += 1) {
      // Stop at the first short page; preserve cancellation between pages.
      // eslint-disable-next-line no-await-in-loop
      const response = await ConversationAPI.getKanbanColumn({
        label: props.label,
        page: number,
        perPage: PER_PAGE,
        ...props.filters,
        signal: controller.signal,
      });
      if (generation !== requestGeneration) return;
      const rows = response?.data?.data?.payload || [];
      rows.forEach(row => refreshed.set(row.id, row));
      nextPage = number + 1;
      if (rows.length < PER_PAGE) {
        reachedEnd = true;
        break;
      }
    }
    cards.value = [...refreshed.values()];
    seenIds = new Set(refreshed.keys());
    page.value = nextPage;
    endReached.value = reachedEnd;
  } catch {
    if (generation === requestGeneration) {
      failed.value = true;
      failedRefresh = true;
    }
  } finally {
    if (generation === requestGeneration) {
      loading.value = false;
      if (pendingRefresh) refreshExisting();
    }
  }
};
const retry = () => (failedRefresh ? refreshExisting() : fetchNext());
watch(() => props.refreshToken, refreshExisting);

const resetAndFetch = () => {
  generation += 1;
  controller?.abort();
  pendingRefresh = false;
  failedRefresh = false;
  cards.value = [];
  seenIds = new Set();
  page.value = 1;
  endReached.value = false;
  loading.value = false;
  fetchNext();
};

watch(() => props.filters, resetAndFetch, { deep: true });

// Load lazily when a collapsed column is expanded.
watch(
  () => props.collapsed,
  isCollapsed => {
    if (!isCollapsed && pendingRefresh) refreshExisting();
    else if (!isCollapsed && cards.value.length === 0) fetchNext();
  }
);

// Attach/detach the infinite-scroll observer whenever the sentinel appears
// (it is removed while the column is collapsed).
watch(sentinel, el => {
  if (observer) {
    observer.disconnect();
    observer = null;
  }
  if (el && typeof IntersectionObserver !== 'undefined') {
    observer = new IntersectionObserver(entries => {
      if (!failed.value && entries.some(entry => entry.isIntersecting))
        fetchNext();
    });
    observer.observe(el);
  }
});

const onDrop = e => {
  const id = Number(e.dataTransfer.getData('text/plain'));
  // MOD-7 v2: source label set by KanbanCard.onDragStart ('' => "Sin etiqueta").
  const rawFrom = e.dataTransfer.getData('application/x-from-label');
  const fromLabel = rawFrom === '' ? null : rawFrom;
  // Current labels of the dragged card so the board computes "move" precisely.
  if (!Number.isSafeInteger(id) || id <= 0 || fromLabel === props.label) return;
  emit('drop-card', {
    conversationId: id,
    toLabel: props.label,
    fromLabel,
  });
};

const onCardClick = id => emit('card-click', id);

onMounted(() => {
  if (!props.collapsed) fetchNext();
});

onBeforeUnmount(() => {
  generation += 1;
  controller?.abort();
  if (observer) {
    observer.disconnect();
    observer = null;
  }
});
</script>

<template>
  <!-- Collapsed: narrow strip, click to expand. No cards loaded. -->
  <div
    v-if="collapsed"
    data-testid="kanban-column"
    class="flex flex-col items-center gap-2 w-11 shrink-0 h-full bg-n-slate-2 border border-n-weak rounded-xl py-2 cursor-pointer hover:border-n-slate-6"
    :title="t('CHAT_LIST.TMK.EXPAND_COLUMN', { column: title })"
    @click="emit('toggle-collapse', label)"
    @drop.prevent="onDrop"
    @dragover.prevent
  >
    <span class="text-xs text-n-slate-11 tabular-nums">{{ headerCount }}</span>
    <span
      class="mt-1 text-xs font-medium text-n-slate-12 truncate max-h-40 [writing-mode:vertical-rl]"
    >
      {{ title }}
    </span>
  </div>

  <!-- Expanded: full column. -->
  <div
    v-else
    data-testid="kanban-column"
    class="flex flex-col w-72 shrink-0 h-full bg-n-slate-2 border border-n-weak rounded-xl"
    @drop.prevent="onDrop"
    @dragover.prevent
  >
    <div
      class="flex items-center gap-1.5 px-3 py-3 border-b border-n-weak shrink-0"
    >
      <span
        class="text-sm font-medium text-n-slate-12 truncate"
        :title="label === null ? t('CHAT_LIST.TMK.UNLABELED_DROP_HINT') : title"
      >
        {{ title }}
      </span>
      <div class="ml-auto flex items-center gap-0.5 shrink-0">
        <span
          data-testid="kanban-column-count"
          class="rounded-md bg-n-slate-3 px-1.5 py-0.5 text-xs text-n-slate-11 tabular-nums mr-1"
          :title="
            countStatus === 'error'
              ? t('CHAT_LIST.TMK.LOCAL_COUNT')
              : t('CHAT_LIST.TMK.REAL_COUNT')
          "
        >
          {{ headerCount }}
        </span>
        <button
          type="button"
          :title="t('CHAT_LIST.TMK.MOVE_LEFT')"
          :aria-label="t('CHAT_LIST.TMK.MOVE_LEFT')"
          class="w-5 h-5 flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 leading-none"
          @click.stop="emit('move-column', { label, dir: 'left' })"
        >
          ‹
        </button>
        <button
          type="button"
          :title="t('CHAT_LIST.TMK.MOVE_RIGHT')"
          :aria-label="t('CHAT_LIST.TMK.MOVE_RIGHT')"
          class="w-5 h-5 flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 leading-none"
          @click.stop="emit('move-column', { label, dir: 'right' })"
        >
          ›
        </button>
        <button
          type="button"
          :title="t('CHAT_LIST.TMK.COLLAPSE_COLUMN')"
          :aria-label="t('CHAT_LIST.TMK.COLLAPSE_COLUMN')"
          class="w-5 h-5 flex items-center justify-center text-n-slate-10 hover:text-n-slate-12 leading-none"
          @click.stop="emit('toggle-collapse', label)"
        >
          –
        </button>
      </div>
    </div>

    <div class="flex flex-col gap-2.5 p-2.5 overflow-y-auto grow">
      <KanbanCard
        v-for="conversation in cards"
        :key="conversation.id"
        :conversation="conversation"
        :column-label="label"
        :attention-labels="attentionLabels"
        @card-click="onCardClick"
      />
      <div
        v-if="failed"
        role="alert"
        class="flex flex-col gap-2 p-3 text-sm text-n-ruby-11"
      >
        {{ t('CHAT_LIST.FETCH_ERROR') }}
        <button
          data-testid="kanban-retry"
          class="text-left underline"
          @click="retry"
        >
          {{ t('CHAT_LIST.TMK.RETRY') }}
        </button>
      </div>
      <p v-else-if="loading" class="p-3 text-xs text-n-slate-10">
        {{ t('CHAT_LIST.TMK.LOADING') }}
      </p>
      <p v-else-if="!cards.length" class="p-4 text-sm text-n-slate-10">
        {{ t('CHAT_LIST.TMK.EMPTY_COLUMN') }}
      </p>
      <div ref="sentinel" class="h-1 w-full shrink-0" />
    </div>
  </div>
</template>
